import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePunditUserContext } from "@circle-react/contexts";
import {
  HELP_WIDGET_FAB_VISIBLE_LOCAL_STORAGE_KEY,
  HELP_WIDGET_IS_EXPANDED_LOCAL_STORAGE_KEY,
  HELP_WIDGET_IS_OPEN_LOCAL_STORAGE_KEY,
  HELP_WIDGET_IS_SIDEBAR_OPEN_LOCAL_STORAGE_KEY,
  HELP_WIDGET_VIRTUAL_CONVERSATION_TYPE_LOCAL_STORAGE_KEY,
  HELP_WIDGET_VIRTUAL_MESSAGES_LOCAL_STORAGE_KEY,
} from "../constants";
import type {
  HelpWidgetContextValue,
  HelpWidgetProviderProps,
  LeaveWarningTrigger,
} from "../types/HelpWidgetContextTypes";
import type {
  VirtualConversationType,
  VirtualMessage,
} from "../types/VirtualMessage";

export const HelpWidgetContext = createContext<HelpWidgetContextValue>({
  isOpen: false,
  toggleWidget: () => {},
  openWidget: () => {},
  isExpanded: false,
  isFabVisible: true,
  isShowingLeaveWarning: false,
  isSidebarOpen: false,
  leaveWarningTrigger: null,
  expandWidget: () => {},
  collapseWidget: () => {},
  currentSpaceProperties: {
    name: "",
    emoji: "",
    custom_emoji_url: "",
    custom_emoji_dark_url: "",
  },
  setCurrentSpaceProperties: () => {},
  setIsShowingLeaveWarning: () => {},
  setLeaveWarningTrigger: () => {},
  expandSideBar: () => {},
  collapseSideBar: () => {},
  hideFab: () => {},
  showFab: () => {},
  shouldDisplayOnboarding: false,
  virtualMessages: [],
  virtualConversationType: null,
  addVirtualMessage: () => {},
  setVirtualMessages: () => {},
  clearVirtualMessages: () => {},
  setVirtualConversationType: () => {},
});

HelpWidgetContext.displayName = "HelpWidgetContext";

export const useHelpWidgetContext = () => {
  const context = useContext(HelpWidgetContext);
  if (!context) {
    throw new Error(
      "useHelpWidgetContext must be used within a HelpWidgetContextProvider",
    );
  }
  return context;
};

export const HelpWidgetContextProvider = ({
  children,
}: HelpWidgetProviderProps) => {
  const { isViewOnlyMasquerading, currentCommunityMember } =
    usePunditUserContext();
  const onWidgetClose = useRef<(() => void) | null>(null);

  const shouldDisplayOnboarding =
    currentCommunityMember?.preferences?.has_seen_widget_onboarding === false; // Strict equality is on purpose here, we want to display onboarding only when `has_seen_widget_onboarding` is equal to false

  const [isOpen, setIsOpen] = useState(() => {
    if (isViewOnlyMasquerading) return false;
    const storedIsOpen = localStorage.getItem(
      HELP_WIDGET_IS_OPEN_LOCAL_STORAGE_KEY,
    );
    return storedIsOpen === "true";
  });

  const [isExpanded, setIsExpanded] = useState(() => {
    const storedIsExpanded = localStorage.getItem(
      HELP_WIDGET_IS_EXPANDED_LOCAL_STORAGE_KEY,
    );
    return storedIsExpanded === "true";
  });

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const storedIsSidebarOpen = localStorage.getItem(
      HELP_WIDGET_IS_SIDEBAR_OPEN_LOCAL_STORAGE_KEY,
    );
    return storedIsSidebarOpen === "true";
  });

  const [isFabVisible, setIsFabVisible] = useState(() => {
    const storedIsFabVisible = localStorage.getItem(
      HELP_WIDGET_FAB_VISIBLE_LOCAL_STORAGE_KEY,
    );
    return storedIsFabVisible === null ? true : storedIsFabVisible === "true";
  });

  const [isShowingLeaveWarning, setIsShowingLeaveWarning] = useState(false);
  const [leaveWarningTrigger, setLeaveWarningTrigger] =
    useState<LeaveWarningTrigger | null>(null);

  const [virtualMessages, setVirtualMessages] = useState<VirtualMessage[]>(
    () => {
      try {
        const stored = localStorage.getItem(
          HELP_WIDGET_VIRTUAL_MESSAGES_LOCAL_STORAGE_KEY,
        );
        return stored ? (JSON.parse(stored) as VirtualMessage[]) : [];
      } catch {
        return [];
      }
    },
  );

  const [virtualConversationType, setVirtualConversationType] =
    useState<VirtualConversationType | null>(() => {
      try {
        const stored = localStorage.getItem(
          HELP_WIDGET_VIRTUAL_CONVERSATION_TYPE_LOCAL_STORAGE_KEY,
        );
        return stored ? (stored as VirtualConversationType) : null;
      } catch {
        return null;
      }
    });

  useEffect(() => {
    localStorage.setItem(
      HELP_WIDGET_IS_EXPANDED_LOCAL_STORAGE_KEY,
      isExpanded.toString(),
    );
  }, [isExpanded]);

  useEffect(() => {
    localStorage.setItem(
      HELP_WIDGET_IS_OPEN_LOCAL_STORAGE_KEY,
      isOpen.toString(),
    );
  }, [isOpen]);

  useEffect(() => {
    localStorage.setItem(
      HELP_WIDGET_IS_SIDEBAR_OPEN_LOCAL_STORAGE_KEY,
      isSidebarOpen.toString(),
    );
  }, [isSidebarOpen]);

  useEffect(() => {
    localStorage.setItem(
      HELP_WIDGET_FAB_VISIBLE_LOCAL_STORAGE_KEY,
      isFabVisible.toString(),
    );
  }, [isFabVisible]);

  useEffect(() => {
    localStorage.setItem(
      HELP_WIDGET_VIRTUAL_MESSAGES_LOCAL_STORAGE_KEY,
      JSON.stringify(virtualMessages),
    );
  }, [virtualMessages]);

  useEffect(() => {
    if (virtualConversationType) {
      localStorage.setItem(
        HELP_WIDGET_VIRTUAL_CONVERSATION_TYPE_LOCAL_STORAGE_KEY,
        virtualConversationType,
      );
    }
  }, [virtualConversationType]);

  const [currentSpaceProperties, setCurrentSpaceProperties] = useState({
    name: "",
    emoji: "",
    custom_emoji_url: "",
    custom_emoji_dark_url: "",
  });

  const expandWidget = useCallback(() => setIsExpanded(true), []);
  const collapseWidget = useCallback(() => setIsExpanded(false), []);
  const expandSideBar = useCallback(() => setIsSidebarOpen(true), []);
  const collapseSideBar = useCallback(() => setIsSidebarOpen(false), []);
  const hideFab = useCallback(() => setIsFabVisible(false), []);
  const showFab = useCallback(() => setIsFabVisible(true), []);

  const toggleWidget = useCallback(
    ({ shouldTriggerOnWidgetClose = false } = {}) => {
      if (isViewOnlyMasquerading) {
        return;
      }
      if (
        shouldTriggerOnWidgetClose &&
        isOpen &&
        onWidgetClose.current !== null
      ) {
        onWidgetClose.current();
      } else {
        const willBeOpen = !isOpen;
        setIsOpen(willBeOpen);
      }
    },
    [isViewOnlyMasquerading, isOpen],
  );

  const openWidget = useCallback(() => setIsOpen(true), []);

  const addVirtualMessage = useCallback(
    (message: VirtualMessage) => setVirtualMessages(prev => [...prev, message]),
    [],
  );

  const clearVirtualMessages = useCallback(() => {
    setVirtualMessages([]);
    setVirtualConversationType(null);
    localStorage.removeItem(HELP_WIDGET_VIRTUAL_MESSAGES_LOCAL_STORAGE_KEY);
    localStorage.removeItem(
      HELP_WIDGET_VIRTUAL_CONVERSATION_TYPE_LOCAL_STORAGE_KEY,
    );
  }, []);

  const contextValue = useMemo(
    () => ({
      isOpen,
      toggleWidget,
      openWidget,
      isExpanded,
      isFabVisible,
      isShowingLeaveWarning,
      isSidebarOpen,
      leaveWarningTrigger,
      expandWidget,
      collapseWidget,
      currentSpaceProperties,
      setCurrentSpaceProperties,
      setIsShowingLeaveWarning,
      setLeaveWarningTrigger,
      expandSideBar,
      collapseSideBar,
      hideFab,
      showFab,
      shouldDisplayOnboarding,
      addVirtualMessage,
      clearVirtualMessages,
      setVirtualConversationType,
      setVirtualMessages,
      virtualMessages,
      virtualConversationType,
    }),
    [
      isOpen,
      toggleWidget,
      openWidget,
      isExpanded,
      isFabVisible,
      isShowingLeaveWarning,
      isSidebarOpen,
      leaveWarningTrigger,
      expandWidget,
      collapseWidget,
      currentSpaceProperties,
      expandSideBar,
      collapseSideBar,
      hideFab,
      showFab,
      shouldDisplayOnboarding,
      addVirtualMessage,
      clearVirtualMessages,
      virtualMessages,
      virtualConversationType,
    ],
  );

  return (
    <HelpWidgetContext.Provider value={contextValue}>
      {children}
    </HelpWidgetContext.Provider>
  );
};
